'use client';

import { useState } from 'react';
import AppImage from '@/components/ui/AppImage';

interface Partner {
  id: number;
  name: string;
  country: string;
  logo: string;
  alt: string;
  description: string;
  programs: string[];
}

const PartnershipsSection = () => {
  const [selectedPartner, setSelectedPartner] = useState<Partner | null>(null);

  const partners: Partner[] = [
  {
    id: 1,
    name: 'Technical University of Munich',
    country: 'Germany',
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1bd0acd09-1772955342762.png",
    alt: 'Modern university campus building with glass facade and students walking',
    description: 'Top-ranked technical university offering world-class engineering and technology programs with strong industry connections.',
    programs: ['Engineering', 'Computer Science', 'Business']
  },
  {
    id: 2,
    name: 'National University of Singapore',
    country: 'Singapore',
    logo: "https://images.unsplash.com/photo-1698047562291-3d8b3c623126",
    alt: 'Contemporary Asian university building with distinctive architecture and green landscaping',
    description: 'Asia\'s leading global university known for research excellence and innovation-driven education.',
    programs: ['Business', 'Engineering', 'Life Sciences']
  },
  {
    id: 3,
    name: 'Sorbonne University',
    country: 'France',
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_141ca20ad-1772235925390.png",
    alt: 'Historic European university building with classical architecture and courtyard',
    description: 'Prestigious French institution with centuries of academic excellence in humanities and sciences.',
    programs: ['Arts', 'Sciences', 'Medicine']
  },
  {
    id: 4,
    name: 'Waseda University',
    country: 'Japan',
    logo: "https://images.unsplash.com/photo-1600584435011-29701e8058f5",
    alt: 'Japanese university campus with traditional and modern architectural blend',
    description: 'Leading private university in Japan with strong international programs and cultural exchange opportunities.',
    programs: ['International Studies', 'Business', 'Engineering']
  },
  {
    id: 5,
    name: 'Peking University',
    country: 'China',
    logo: "https://images.unsplash.com/photo-1705725541402-2fcc0acf69d3",
    alt: 'Chinese university campus with traditional pagoda-style buildings and modern facilities',
    description: 'China\'s premier institution offering comprehensive programs with global perspective and research opportunities.',
    programs: ['Economics', 'Computer Science', 'International Relations']
  },
  {
    id: 6,
    name: 'American University of Sharjah',
    country: 'UAE',
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_13f0b3b3b-1772235921606.png",
    alt: 'Modern Middle Eastern university campus with contemporary Islamic architecture',
    description: 'Leading independent university in the Gulf region with American-style education and diverse student body.',
    programs: ['Business', 'Engineering', 'Architecture']
  }];


  return (
    <>
      <section className="bg-background py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Our University Partners
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              Exclusive partnerships with world-renowned institutions across 9 countries, ensuring you have access to the best educational opportunities globally.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {partners.map((partner) =>
            <div
              key={partner.id}
              className="bg-card border border-border rounded-xl overflow-hidden hover:shadow-hover transition-all duration-300 transform hover:-translate-y-1 cursor-pointer"
              onClick={() => setSelectedPartner(partner)}>

                <div className="relative h-48 overflow-hidden bg-muted">
                  <AppImage
                  src={partner.logo}
                  alt={partner.alt}
                  className="w-full h-full object-cover" />

                </div>
                <div className="p-6">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-bold text-foreground">{partner.name}</h3>
                    <span className="px-3 py-1 bg-primary/10 text-primary text-xs rounded-full font-semibold">
                      {partner.country}
                    </span>
                  </div>
                  <p className="text-muted-foreground text-sm line-clamp-2 mb-4">
                    {partner.description}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {partner.programs.slice(0, 2).map((program, idx) =>
                  <span key={idx} className="px-2 py-1 bg-muted text-foreground text-xs rounded">
                        {program}
                      </span>
                  )}
                    {partner.programs.length > 2 &&
                  <span className="px-2 py-1 bg-muted text-foreground text-xs rounded">
                        +{partner.programs.length - 2}
                      </span>
                  }
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {selectedPartner &&
      <div
        className="fixed inset-0 bg-foreground/80 z-50 flex items-center justify-center p-4"
        onClick={() => setSelectedPartner(null)}>

          <div
          className="bg-card rounded-2xl max-w-2xl w-full"
          onClick={(e) => e.stopPropagation()}>

            <div className="relative h-64 overflow-hidden">
              <AppImage
              src={selectedPartner.logo}
              alt={selectedPartner.alt}
              className="w-full h-full object-cover" />

              <button
              onClick={() => setSelectedPartner(null)}
              className="absolute top-4 right-4 w-10 h-10 bg-card rounded-full flex items-center justify-center hover:bg-muted transition-colors">

                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-8">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-2xl font-bold text-foreground">{selectedPartner.name}</h3>
                <span className="px-4 py-2 bg-primary/10 text-primary rounded-lg font-semibold">
                  {selectedPartner.country}
                </span>
              </div>
              <p className="text-foreground leading-relaxed mb-6">{selectedPartner.description}</p>
              <div>
                <h4 className="text-sm font-semibold text-foreground mb-3">Available Programs:</h4>
                <div className="flex flex-wrap gap-2">
                  {selectedPartner.programs.map((program, idx) =>
                <span key={idx} className="px-4 py-2 bg-secondary/10 text-secondary rounded-lg text-sm font-medium">
                      {program}
                    </span>
                )}
                </div>
              </div>
            </div>
          </div>
        </div>
      }
    </>);

};

export default PartnershipsSection;